<template>
	<div class="touch-card-data">
		<div v-if="!rows.length" class="text-center pa-6 text-disabled">{{ t('NoData') }}</div>

		<div class="touch-card-data__sort" v-else>
			<SelectControl class="touch-card-data__sort-select" :label="t('SortBy')" :items="sortOptions" :sort-items="false" :clearable="false" v-model="sortBy" />
			<v-btn class="touch-card-data__sort-order" icon variant="tonal" size="small" color="primary" @click="toggleSortOrder">
				<md-icon>{{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</md-icon>
			</v-btn>
		</div>

		<v-card v-for="(row, index) in sortedRows" :key="getRowKey(row, index)">
			<div class="mb-2">
				<div class="title">{{ getValue(row, props.titleField) }}</div>
				<div v-if="subtitle(row)" class="text-caption text-medium-emphasis">{{ subtitle(row) }}</div>
			</div>

			<div class="touch-card-data__metrics" :class="`touch-card-data__metrics--${props.metricLayout}`">
				<div v-for="column in normalizedColumns" :key="column.field" class="touch-card-data__metric">
					<span class="text-caption text-uppercase text-medium-emphasis">{{ column.label }}</span>
					<span class="touch-card-data__evo-container">
						<template v-if="column.fromField">
							<div class="touch-card-data__gap">
								{{ formatValue(row, column, column.fromField) }}
								<span class="text-disabled mx-1">→</span>
								<span class="font-weight-bold">{{ formatValue(row, column) }}</span>
							</div>
						</template>
						<span v-if="column.evoField && hasEvo(row, column)" class="touch-card-data__evo" :class="evoClass(row, column)">
							{{ formatEvo(row, column) }}
						</span>
					</span>
				</div>
			</div>
		</v-card>
	</div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useTranslator } from '@/utils/useTranslator';
import SelectControl from '@/components/controls/SelectControl.vue';

const t = useTranslator();

const props = defineProps({
	rowData: { type: Array, default: () => [] },
	columns: { type: Array, default: () => [] },
	titleField: { type: String, default: 'label' },
	subtitleFields: { type: Array, default: () => ['reference', 'ean'] },
	rowKey: { type: String, default: 'id' },
	metricLayout: { type: String, default: 'grid' },
	sortStorageKey: { type: String, default: null },
});

const rows = computed(() => (Array.isArray(props.rowData) ? props.rowData : []));

const normalizedColumns = computed(() =>
	(Array.isArray(props.columns) ? props.columns : []).map((column) => ({
		field: column.field,
		label: column.label || column.field,
		suffix: column.suffix || '',
		decimals: column.decimals ?? 0,
		evoField: column.evoField || null,
		fromField: column.fromField || null,
	}))
);

function getValue(row, field) {
	return row && field ? (row[field] ?? '') : '';
}

function getRowKey(row, index) {
	return row?.[props.rowKey] ?? index;
}

function subtitle(row) {
	return (props.subtitleFields || [])
		.map((field) => getValue(row, field))
		.filter((value) => value !== '' && value !== null && value !== undefined)
		.join(' · ');
}

function sortStorageField(suffix) {
	return props.sortStorageKey ? `${props.sortStorageKey}.${suffix}` : null;
}

const sortBy = ref((props.sortStorageKey && localStorage.getItem(sortStorageField('sortBy'))) || props.titleField);
const sortOrder = ref((props.sortStorageKey && localStorage.getItem(sortStorageField('sortOrder'))) || 'asc');

watch(sortBy, (value) => {
	if (props.sortStorageKey) localStorage.setItem(sortStorageField('sortBy'), value);
});
watch(sortOrder, (value) => {
	if (props.sortStorageKey) localStorage.setItem(sortStorageField('sortOrder'), value);
});

function toggleSortOrder() {
	sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
}

const sortOptions = computed(() => {
	const options = [{ id: props.titleField, name: t('LabelAZ') }];
	normalizedColumns.value.forEach((column) => {
		options.push({ id: column.field, name: `${column.label} ${t('P2')}` });
		if (column.evoField) options.push({ id: column.evoField, name: `${t('Gap')} ${column.label}` });
	});
	return options;
});

const sortedRows = computed(() => {
	const key = sortBy.value;
	const dir = sortOrder.value === 'asc' ? 1 : -1;
	return [...rows.value].sort((a, b) => {
		if (key === props.titleField) return String(getValue(a, props.titleField)).localeCompare(String(getValue(b, props.titleField))) * dir;
		return ((Number(a[key]) || 0) - (Number(b[key]) || 0)) * dir;
	});
});

function formatNumber(value, decimals) {
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return '0';
	const rounded = decimals > 0 ? Math.round(numeric * Math.pow(10, decimals)) / Math.pow(10, decimals) : Math.round(numeric);
	return rounded.toLocaleString();
}

function formatValue(row, column, field = column.field) {
	return `${formatNumber(getValue(row, field), column.decimals)}${column.suffix}`;
}

function hasEvo(row, column) {
	const value = row?.[column.evoField];
	return value !== null && value !== undefined && Number.isFinite(Number(value));
}

function evoClass(row, column) {
	return Number(row[column.evoField]) >= 0 ? 'positive' : 'negative';
}

function formatEvo(row, column) {
	const value = Number(row[column.evoField]);
	const sign = value > 0 ? '+' : '';
	return `${sign}${Math.round(value * 10) / 10}%`;
}
</script>

<style scoped lang="scss">
@use '@/styles/variables' as *;

.touch-card-data {
	display: flex;
	flex-direction: column;
	gap: 8px;

	&__sort {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	&__sort-select {
		flex: 1 1 auto;
		max-width: 100%;
	}

	&__metrics {
		display: grid;

		&--grid {
			grid-template-columns: repeat(2, 1fr);
		}

		&--rows {
			grid-template-columns: 1fr;
		}
	}

	&__metric {
		display: flex;
		flex-direction: column;
		border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
		padding-top: 2px;
		line-height: 28px;

		&:first-child {
			border-top: thin dashed rgba(var(--v-border-color), var(--v-border-opacity));
		}
	}

	&__metrics--rows &__metric {
		flex-direction: row;
		align-items: baseline;
		justify-content: space-between;
		gap: 8px;
	}

	&__evo-container {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 4px;
		flex: 1;
	}

	&__evo {
		display: inline-flex;
		align-items: center;
		justify-content: flex-end;
		font-size: $font-size-small;
		font-weight: 600;
		width: 60px;

		&.positive {
			color: $color-success;
		}

		&.negative {
			color: $color-error;
		}
	}

	.v-card {
		background: rgb(var(--v-theme-surface));
		border: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
		border-radius: 12px;
		padding: 12px;
		box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

		.title {
			font-size: $font-size;
			line-height: $font-size-extra-large;
			font-weight: 600;
			white-space: nowrap;
			text-overflow: ellipsis;
			overflow: hidden;
		}
	}
}
</style>
